import { cssColor } from './color.ts';
import { sampleColors } from './color_gradient.ts';
import type { Rect } from './flex_label.ts';

/**
 * The legacy `GaugeView`: a 270° dial of coloured ticks, a dim track inside it and a value arc
 * coloured by its angle. The dial is as large as fits its box and is centred in it.
 */

/** The dial reaches this far below its horizontal centre line on both sides (45°). */
const OVERSHOOT = Math.PI / 4;
const TICK_INNER = 0.85;
const ARC_WIDTH = 0.075;
const TICK_WIDTH = 2;
const TRACK_COLOR = 'rgba(255, 255, 255, 0.196)';

export interface GaugeGeometry {
    radius: number;
    centerX: number;
    centerY: number;
    /** Where the dial is drawn inside its box (the legacy render bounds). */
    bounds: Rect;
}

export interface GaugeValue {
    value: number | null;
    min: number;
    max: number;
    /** The gradient along the dial, from the minimum to the maximum. */
    colors: readonly string[];
    /** The unfilled part's colour, or `none` for none; left out, the legacy dim track. */
    track?: string | undefined;
}

/** Size and position of the dial in a box of `width` × `height`. */
export function gaugeGeometry(width: number, height: number) : GaugeGeometry {
    const sine = Math.sin(OVERSHOOT);
    const radius = Math.max(0, Math.min(width / 2, height / (1 + sine)));
    const dialHeight = radius * (1 + sine);

    return {
        radius,
        centerX: width / 2,
        centerY: height / 2 + (radius * (1 - sine)) / 2,
        bounds: { left: (width - 2 * radius) / 2, top: (height - dialHeight) / 2, width: 2 * radius, height: dialHeight },
    };
}

/** Angle of a position along the dial (0 to 1), in canvas radians: 135° at the start, 405° at the end. */
export function gaugeAngle(position: number) : number {
    return position * (Math.PI + 2 * OVERSHOOT) - OVERSHOOT + Math.PI;
}

/**
 * The colour of the value arc at each whole degree, starting at 0° (the positive x axis), as the
 * legacy sweep shader assigns them: the dial's gradient spread over the 270° from 135° on.
 */
export function sweepColors(colors: readonly string[]) : string[] {
    const startDegrees = 135;
    const endDegrees = 405;
    const spread = sampleColors(colors, endDegrees - startDegrees);
    const result: string[] = [];
    for (let degree = 0; degree < 360; degree++) {
        result.push(spread[((degree + (endDegrees - 180) + 360) % 360) % spread.length] ?? 'white');
    }

    return result;
}

const sweepCache = new WeakMap<readonly string[], string[]>();

/** Draws the dial in `box`. */
export function drawGauge(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, box: Rect, gauge: GaugeValue) : void {
    const geometry = gaugeGeometry(box.width, box.height);
    const { radius } = geometry;
    if (radius <= 0) {
        return;
    }

    const centerX = box.left + geometry.centerX;
    const centerY = box.top + geometry.centerY;

    ctx.save();

    // Ticks, one per 1.5 px of radius, from the rim to 85 % of it.
    const tickCount = Math.floor(radius / 1.5);
    const tickColors = sampleColors(gauge.colors, tickCount + 1);
    ctx.lineWidth = TICK_WIDTH;
    ctx.lineCap = 'butt';
    for (let index = 0; index <= tickCount; index++) {
        const angle = gaugeAngle(index / tickCount);
        ctx.strokeStyle = tickColors[index] ?? 'white';
        ctx.beginPath();
        ctx.moveTo(centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius);
        ctx.lineTo(centerX + Math.cos(angle) * radius * TICK_INNER, centerY + Math.sin(angle) * radius * TICK_INNER);
        ctx.stroke();
    }

    // The track, then the value arc on top of it.
    const arcRadius = radius * (TICK_INNER - ARC_WIDTH);
    ctx.lineWidth = radius * ARC_WIDTH;
    if (gauge.track !== 'none') {
        ctx.strokeStyle = gauge.track === undefined ? TRACK_COLOR : cssColor(gauge.track);
        ctx.beginPath();
        ctx.arc(centerX, centerY, arcRadius, gaugeAngle(0), gaugeAngle(1));
        ctx.stroke();
    }

    if (gauge.value !== null && gauge.max > gauge.min) {
        // Clamped to the dial; the legacy gauge drew a wrong arc for values outside it.
        const position = Math.min(1, Math.max(0, (gauge.value - gauge.min) / (gauge.max - gauge.min)));
        if (position > 0) {
            let colors = sweepCache.get(gauge.colors);
            if (!colors) {
                colors = sweepColors(gauge.colors);
                sweepCache.set(gauge.colors, colors);
            }

            const gradient = ctx.createConicGradient(0, centerX, centerY);
            for (const [degree, color] of colors.entries()) {
                gradient.addColorStop(degree / 360, color);
            }

            ctx.strokeStyle = gradient;
            ctx.beginPath();
            ctx.arc(centerX, centerY, arcRadius, gaugeAngle(0), gaugeAngle(position));
            ctx.stroke();
        }
    }

    ctx.restore();
}
